import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSEventLoop: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Event Loop &amp; Concurrency</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    How a single-threaded language juggles timers, network calls, and clicks at once — and why a Promise callback always beats a <code>setTimeout(0)</code>.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="18 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>JavaScript is single-threaded — one call stack, one thing at a time. Yet apps seem to do many things at once:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['API calls', 'User clicks', 'Timers', 'Video playback', 'WebSocket messages', 'Thousands of requests'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Callout tone="info" title="The puzzle">
    How can a single-threaded language appear to do so many things simultaneously? The answer is the <Mark>event loop</Mark>.
   </Callout>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why This Needs Explaining</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Start");\nsetTimeout(() => console.log("Timer Done"), 3000);\nconsole.log("End");` }]} defaultLanguage="javascript" id="el-why" />
   <Output>{`Start\nEnd\nTimer Done`}</Output>
   <Para>Beginners expect a 3-second pause before <Code>Timer Done</Code>. Instead <Code>End</Code> prints first and JavaScript never waited. Understanding why requires the runtime architecture.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">The <Mark>event loop</Mark> is a scheduler that continuously checks whether the call stack is empty and, when it is, moves pending tasks from the queues into execution.</p>
   </div>
  </section>

  {/* 4. Core Idea: single thread + call stack */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea: One Call Stack</SectionHeading>
   <Para>JavaScript runs code on a single <Mark>call stack</Mark> — a stack of currently executing functions. Only one runs at a time:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function a() { b(); }\nfunction b() { c(); }\nfunction c() { console.log("Hello"); }\na();` }]} defaultLanguage="javascript" id="el-stack" />
   <CodeBlock snippets={[{ language: 'text', code: `push a → push b → push c → run c → pop c → pop b → pop a\n\n| c |\n| b |   →  empty again after each returns\n| a |` }]} defaultLanguage="text" id="el-stack-visual" />
   <Para>If a slow operation (say a 30-second file read) ran on this stack, the whole app would freeze — no clicks, scrolls, timers, or responses. JavaScript needed a way to start slow work, keep running, and handle the result later.</Para>
  </section>

  {/* 5. Internal Working: runtime + architecture */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <Para>JavaScript can&apos;t make network calls, run timers, or read files <em>by itself</em>. Those capabilities come from the <Mark>runtime</Mark>, which does the waiting on its behalf:</Para>
   <TwoUp
    left={{ title: 'Browser provides', items: ['setTimeout / setInterval', 'fetch', 'DOM events', 'Web APIs'] }}
    right={{ title: 'Node.js provides', items: ['File system (libuv)', 'Network APIs', 'Thread pool', 'OS access'] }}
   />
   <Para>The full picture has five pieces working together:</Para>
   <div className="flex flex-wrap gap-2">{['Call stack', 'Browser / Node APIs', 'Callback (macrotask) queue', 'Microtask queue', 'Event loop'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `   ┌──────────────┐      offload      ┌──────────────────┐\n   │  Call Stack  │ ───────────────▶ │ Browser/Node APIs │\n   └──────┬───────┘                  └─────────┬────────┘\n          ▲                                    │ done\n          │ push when stack empty              ▼\n   ┌──────┴───────┐   drains first   ┌──────────────────┐\n   │  Event Loop  │ ◀─────────────── │  Microtask queue  │\n   └──────┬───────┘                  └──────────────────┘\n          │ then one                 ┌──────────────────┐\n          └───────────────────────── │  Callback queue  │\n                                      └──────────────────┘` }]} defaultLanguage="text" id="el-arch" />
   <Para>The loop&apos;s job: is the call stack empty? If yes, run all microtasks, then take one macrotask — and repeat forever.</Para>
  </section>

  {/* 7. Simple Example */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Start");\nsetTimeout(() => console.log("Timer"), 0);\nconsole.log("End");` }]} defaultLanguage="javascript" id="el-simple" />
   <Output>{`Start\nEnd\nTimer`}</Output>
   <ol className="my-4 space-y-1.5 text-[14px] text-gray-700 dark:text-gray-300 list-decimal list-inside">
    <li><Code>Start</Code> logs immediately.</li>
    <li><Code>setTimeout</Code> hands the callback to the browser timer API; JS continues.</li>
    <li><Code>End</Code> logs.</li>
    <li>The timer finishes; its callback enters the callback queue.</li>
    <li>The event loop sees the stack is empty and pushes the callback.</li>
    <li><Code>Timer</Code> logs.</li>
   </ol>
   <Callout tone="warning" title="setTimeout(0) ≠ immediately">
    <Code>setTimeout(fn, 0)</Code> means &quot;run as soon as possible <em>after</em> the current work and the queue ahead of it&quot; — not instantly.
   </Callout>
  </section>

  {/* 8. Deep Example: macrotask vs microtask */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Two Queues</SectionHeading>
   <Para>Completed timer/event callbacks go to the <Mark>callback (macrotask) queue</Mark>. Promise reactions go to a separate, <Mark>higher-priority microtask queue</Mark>.</Para>
   <TwoUp
    left={{ title: 'Macrotask queue', items: ['setTimeout / setInterval', 'DOM events', 'message events', 'Lower priority'] }}
    right={{ title: 'Microtask queue', items: ['Promise.then/catch/finally', 'queueMicrotask', 'await continuations', 'Higher priority'] }}
   />
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Start");\nsetTimeout(() => console.log("Timer"), 0);\nPromise.resolve().then(() => console.log("Promise"));\nconsole.log("End");` }]} defaultLanguage="javascript" id="el-deep" />
   <Output>{`Start\nEnd\nPromise\nTimer`}</Output>
   <Para>Most people expect <Code>Timer</Code> before <Code>Promise</Code>. But after the synchronous code, the event loop drains <Mark>all microtasks</Mark> before touching the macrotask queue — so the Promise wins.</Para>
  </section>

  {/* 9. Production Example: the golden rule + a harder case */}
  <section className="mb-12">
   <SectionHeading n={9}>The Execution-Order Golden Rule</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `Current stack\n   ↓\nALL microtasks\n   ↓\nONE macrotask\n   ↓\nALL microtasks\n   ↓\nnext macrotask ...` }]} defaultLanguage="text" id="el-rule" />
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("A");\nsetTimeout(() => console.log("B"), 0);\nPromise.resolve().then(() => console.log("C"));\nconsole.log("D");` }]} defaultLanguage="javascript" id="el-harder" />
   <Output>{`A\nD\nC\nB`}</Output>
   <Para><Code>A</Code> and <Code>D</Code> are synchronous; <Code>C</Code> is a microtask (runs next); <Code>B</Code> is a macrotask (runs last).</Para>
  </section>

  {/* 10. Engineering Tradeoffs: concurrency vs parallelism */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs: Concurrency vs Parallelism</SectionHeading>
   <TwoUp
    left={{ title: 'Concurrency', items: ['Many tasks make progress together', 'Interleaved on one thread', 'What JavaScript provides'] }}
    right={{ title: 'Parallelism', items: ['Tasks run at the same instant', 'Needs multiple CPU cores', 'Web Workers / worker threads'] }}
   />
   <Callout tone="info" title="Is JavaScript really single-threaded?">
    Yes — JS <em>execution</em> is single-threaded. But the <Mark>runtime</Mark> may use OS threads, thread pools, and network layers behind the scenes (Node uses <Code>libuv</Code>). The language gives you concurrency, not parallelism.
   </Callout>
  </section>

  {/* 11. Performance Considerations: production scaling */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>This model is why Node scales for I/O-heavy work. With 1000 users each running a 500ms DB query, blocking would cost <Code>1000 × 500ms</Code>. Instead Node starts each query, keeps handling other requests, and processes results as they arrive:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Non-blocking: the thread stays free while the DB works\napp.get("/orders", async (req, res) => {\n  const orders = await db.query("SELECT * FROM orders"); // offloaded\n  res.json(orders);\n});` }]} defaultLanguage="javascript" id="el-scale" />
   <Para>The flip side: anything CPU-heavy on the main thread blocks <em>everyone</em>, because there&apos;s only one stack.</Para>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Blocking the event loop">A <Code>while (true) { }</Code> or synchronous CPU loop freezes the entire app.</Callout>
   <Callout tone="warning" title="Heavy synchronous work">Generating a huge report on the main thread stalls all requests — offload to a worker.</Callout>
   <Callout tone="warning" title="Microtask starvation">Endlessly chaining microtasks can starve timers and rendering (see below).</Callout>
   <Callout tone="warning" title="Wrong order assumptions">Assuming <Code>setTimeout(0)</Code> beats a Promise — it doesn&apos;t.</Callout>
  </section>

  {/* 13. Debugging Stories: microtask starvation */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The timer that never fired">
    A recursive Promise chain kept the microtask queue permanently full:
   </Callout>
   <CodeBlock snippets={[{ language: 'javascript', code: `function loop() {\n  Promise.resolve().then(loop); // schedules another microtask, forever\n}\nloop();\n// setTimeout callbacks (macrotasks) never get a turn` }]} defaultLanguage="javascript" id="el-starve" />
   <Para>Because the loop drains <em>all</em> microtasks before any macrotask, timers and rendering never ran — <Mark>microtask starvation</Mark>. Breaking the chain (or using <Code>setTimeout</Code> to yield) restored macrotasks.</Para>
   <Callout tone="info" title="The frozen UI">
    A &quot;random&quot; freeze was a synchronous loop processing a huge array on the main thread. Chunking it across macrotasks (or a Web Worker) let the event loop breathe and the UI stay responsive.
   </Callout>
  </section>

  {/* 14. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is the event loop?', 'Call stack?', 'Callback queue?', 'Microtask queue?', 'Promise vs setTimeout order?', 'Why does Promise win?', 'Does setTimeout(0) run immediately?', 'Is JS single-threaded?', 'Concurrency vs parallelism?', 'Microtask starvation?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Why does a Promise callback run before setTimeout(0)?">
    Promise reactions go on the microtask queue; <Code>setTimeout</Code> goes on the macrotask queue. After the current stack finishes, the event loop drains <em>all</em> microtasks before running a single macrotask — so the Promise runs first.
   </QA>
   <QA q="Does setTimeout(fn, 0) execute immediately?">
    No. It queues <Code>fn</Code> as a macrotask to run as soon as possible <em>after</em> the current synchronous code and all pending microtasks complete — and never before the minimum clamped delay.
   </QA>
   <QA q="Is JavaScript single-threaded?">
    JavaScript execution is single-threaded (one call stack). Asynchronous work is delegated to the runtime, which may use OS threads/thread pools (e.g. Node&apos;s libuv) under the hood. The language itself still runs your code on one thread.
   </QA>
   <QA q="What is the event loop's golden rule of ordering?">
    Run the current stack, then all microtasks, then one macrotask, then all microtasks again, then the next macrotask — repeating forever.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>Think of a restaurant: the <Mark>chef</Mark> is the call stack, the <Mark>waiting orders</Mark> are the queues, and the <Mark>manager</Mark> is the event loop. The manager only hands the chef a new order once the chef is free — and priority orders (microtasks) jump ahead of regular ones (macrotasks).</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     The event loop is a scheduler that continuously checks whether the call stack is empty and moves pending tasks from the microtask and callback queues into execution.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    JavaScript is single-threaded, but the runtime handles async operations and pushes completed work into queues. The event loop continuously moves that work onto the call stack. Microtasks (Promises) outrank macrotasks (timers), which is exactly why Promise callbacks run before <Code>setTimeout</Code> callbacks.
   </Callout>
  </section>
 </div>
);

export default JSEventLoop;
